/* eslint-disable @typescript-eslint/no-explicit-any */
import { forkJoin, from, Observable, of, pipe } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

import { TranslateService } from '@ngx-translate/core';

import { OrgSettingsService } from '@app/orgs/services';
import { OrgHelpMenuService } from '@app/orgs/services/org-help-menu.service';
import { TargetsService } from '@app/target/services/targets.service';
import {
  BrandingService,
  ContextService,
  LDFlagsService,
  TrackerService,
} from '@degreed/lxp/services';

import { WebEnvironmentService } from '@degreed/lxp/services';
import { AuthService, AuthUser } from '@degreed/lxp/services/authorization';

import { LayoutConfiguration } from '@degreed/lxp/ui';
import * as DegreedLayout from './layout-configuration.json';

import { NotificationService } from '@app/notifications/notification.service';
import {
  addAnalyticTrackers,
  NavOptions,
  updateHelpMenu,
  updatei18n,
  updateLxpAdminNavigation,
  updateLxpLearnerNavigation,
  updateNavigationBranding,
  updateNotificationMenuItem,
  updateUrls,
  updateUser,
  updateVisibility,
} from './layout-configuration.utils';
import { PermissionFactory, resolveOrgInfo } from './permission.factory';

/**
 * LayoutConfiguration Service
 *
 * This Angular version (layout-configuration.service.ts) uses RxJS and streams
 * The React version (layout-configuration.hook.ts) does NOT use RxJS
 *
 * Note: The `layout-configuration.utils.ts` is shared between the Angular and React versions
 *
 */

/**
 * Private internal cache of the layout configuration
 */
let layout$: Observable<LayoutConfiguration>;

/**
 * Return a LayoutConfiguration object based on the user's default organization
 * NOTE: Currently we use compiled configuration files: embedded JSON that is runtime transformed.
 *       !! This configuration should be constructed on the server
 *
 * @returns Observable<LayoutConfiguration>
 */
export const buildLayoutConfiguration_v3 = (
  auth: AuthService,
  translate: TranslateService,
  environment: WebEnvironmentService,
  targets: TargetsService,
  featureFlags: LDFlagsService,
  context: ContextService,
  helpMenu: OrgHelpMenuService,
  trackerService: TrackerService,
  brandingService: BrandingService,
  notificationService: NotificationService,
  orgSettingsService: OrgSettingsService
): Observable<LayoutConfiguration> => {
  // Customize the configuration JSON based on the user's organization, i18n and other information
  const buildLayout = pipe(
    filter((user: AuthUser | undefined) => !!user),
    switchMap((user: AuthUser | undefined) => {
      // Gather async data before customizing layout
      const organizationId = resolveOrgInfo(user)[2].organizationId;

      // load org data or defaults if no org
      const supportInfo$ = organizationId
        ? helpMenu.getSupportInfo(organizationId)
        : of(null);
      const learnInSSOUrl$ = organizationId
        ? orgSettingsService
            .getSetting(organizationId, 'LearnInSsoUrl')
            .pipe(map((settings) => settings.value))
        : of(null);
      const featuredPlanId$ = organizationId
        ? targets
            .getBrowseTarget(organizationId)
            .pipe(map((target) => target.targetId))
        : of(null);

      // This may not be necessary, we're loading branding on app init
      const branding$ = organizationId
        ? from(brandingService.loadBranding({ orgId: organizationId }, false))
        : of(null);
      const notificationCounts$ = notificationService.loadShallowCounts();

      return forkJoin([
        of(user),
        supportInfo$,
        featuredPlanId$,
        learnInSSOUrl$,
        branding$,
        notificationCounts$,
      ]);
    }),
    map(
      ([
        authUser,
        supportInfo,
        featuredPlanId,
        learnInSSOUrl,
        branding,
        notificationCounts,
      ]): LayoutConfiguration => {
        const analyticsUrl = '/analytics?tag=1';
        const [, , orgInfo] = resolveOrgInfo(authUser);
        const factory = new PermissionFactory(
          authUser,
          featureFlags,
          context,
          environment
        );
        const navOptions = {
          isProduction: environment.isProduction,
          authUser,
        } as NavOptions;

        let configuration = structuredClone({
          ...DegreedLayout,
        }) as LayoutConfiguration;

        // Update i18n asap to get any placeholder variables used in translations updated
        // in the JSON before any subsequent utils functions are called

        configuration = updateVisibility(configuration) as LayoutConfiguration;

        configuration = updatei18n(configuration, translate, authUser);
        configuration = updateUser(configuration, authUser, environment);
        configuration = updateUrls(
          configuration,
          authUser,
          featuredPlanId,
          learnInSSOUrl,
          analyticsUrl
        );

        configuration = updateLxpAdminNavigation(
          configuration,
          factory.forLxpAdmin(),
          navOptions
        );
        configuration = updateLxpLearnerNavigation(
          configuration,
          factory.forLxpLearner(),
          navOptions
        );
        configuration = updateNavigationBranding(
          configuration,
          branding,
          translate
        );

        configuration = updateHelpMenu(
          configuration,
          orgInfo.organizationId,
          supportInfo
        );
        configuration = addAnalyticTrackers(configuration, trackerService);
        configuration = updateNotificationMenuItem(
          configuration,
          notificationCounts
        );

        return configuration;
      }
    ),
    filter((config): config is LayoutConfiguration => config !== null)
  );

  layout$ ||= auth.authUser$.pipe(buildLayout);

  return layout$;
};
